import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import { CalendarOptions } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { MessageService } from 'primeng/api';
import { Popover, PopoverModule } from 'primeng/popover';
import { SelectButtonModule } from 'primeng/selectbutton';
import { firstValueFrom, forkJoin } from 'rxjs';
import { Settings, SETTINGS } from '../../../../app.settings';
import { notifyAndTrackError } from '../../../../common/error';
import { getItemDescriptionColumn, isStringEmpty, isStringEqual } from '../../../../common/functions';
import { AppInitService } from '../../../../services/app-init.service';
import { AppInsightsService } from '../../../../services/app-insights.service';
import {
  DictionaryCacheService,
  IAppUser,
  ISearchFilterValue,
  ISearchRequest,
  ISearchRequestFilter,
  ISearchSort,
  RequesterDashboardService
} from '../../../../services/insecta';
import { MSGraphService } from '../../../../services/msgraph.service';

@Component({
  selector: 'app-requester-calendar',
  standalone: true,
  imports: [FullCalendarModule, PopoverModule, FormsModule, SelectButtonModule, CommonModule],
  templateUrl: './requester-calendar.component.html'
})
export class RequesterCalendarComponent implements OnInit {
  private settings: Settings = inject(SETTINGS);
  private readonly dictionaryCache = inject(DictionaryCacheService);
  private readonly graphService = inject(MSGraphService);
  private readonly msgService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly appInit = inject(AppInitService);
  private readonly appInsights = inject(AppInsightsService);

  @ViewChild('calendar') calendar!: FullCalendarComponent;
  @ViewChild('op') op!: Popover;

  appUser!: IAppUser;
  userSettingsJson: { [key: string]: any } = {};

  popupRequest: any;
  events: any[] = [];

  isInitialLoadComplete: boolean = false;
  greaterthanDate: string = '';
  lessThandate: string = '';

  pageVars: any = {
    currentUser: ``,
    isEditing: false,
    isReady: false,
    pageId: `Dashboard:Requester:Calendar`,
    orderItemInstances: <any[]>[],
    undeliveravbleDates: <any[]>[],
    undeliveravbleDays: <any[]>[]
  };

  skip: number = 0;
  take?: number = undefined;
  totalCount: number = 0;

  requesterDashboardService: RequesterDashboardService = inject(RequesterDashboardService);
  searchRequest: ISearchRequest = {
    filters: [],
    sorts: [],
    skip: this.skip,
    take: this.take
  };
  tableFilters: ISearchRequestFilter[] = [];

  calViews = [
    { label: 'Month', value: 'dayGridMonth' },
    { label: 'Week', value: 'dayGridWeek' }
  ];
  selCalView = 'dayGridMonth';

  calendarOptions: CalendarOptions = {
    initialView: 'dayGridMonth',
    plugins: [dayGridPlugin, interactionPlugin],
    dayMaxEvents: 3,
    views: { dayGridMonth: { dayMaxEventRows: 3 } },
    headerToolbar: {
      left: 'today',
      center: 'title',
      right: 'prev,next'
    },
    contentHeight: 600,
    eventMouseEnter: (info) => {
      info.el.style.cursor = 'pointer';
      this.popupRequest = this.pageVars.orderItemInstances.find((x: any) => x.id === info.event.id);
      this.op.show(info.jsEvent, info.el);
    },
    eventMouseLeave: () => {
      this.op.hide();
    },
    dayCellDidMount: (info) => {
      const cellDate = this.formatDate(info.date);
      if (this.pageVars.undeliveravbleDates.includes(cellDate)) {
        info.el.style.backgroundColor = '#f0f0f0';
      }
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = dayNames[info.date.getUTCDay()];
      if (this.pageVars.undeliveravbleDays.includes(dayName)) {
        info.el.style.backgroundColor = '#f0f0f0';
      }
    },
    datesSet: (dateInfo) => {
      this.greaterthanDate = dateInfo.startStr.split('T')[0];
      this.lessThandate = dateInfo.endStr.split('T')[0];
      if (this.isInitialLoadComplete) {
        this.loadEventsForDateRange(this.greaterthanDate, this.lessThandate);
      }

      if (dateInfo.view.type === 'dayGridMonth') {
        dateInfo.view.calendar.setOption('dayMaxEvents', 3);
      } else if (dateInfo.view.type === 'dayGridWeek') {
        dateInfo.view.calendar.setOption('dayMaxEvents', 12);
      }
    },
    eventClick: (info) => {
      const orderId = info.event._def.extendedProps['OrderId'];
      console.log(orderId);
      if (orderId) {
        this.router.navigate([`/orders/${orderId}/requester`]);
      } else {
        console.warn('Order ID not found in event properties');
      }
    },
    eventContent: (arg) => {
      const el = document.createElement('span');

      const iconClasses = arg.event?.extendedProps['icons'];
      iconClasses?.forEach((iconClass: string, i: number) => {
        if (iconClass) {
          const icon = document.createElement('i');
          icon.className = `fa-solid ${iconClass}`;
          icon.style.marginLeft = i === 0 ? '2px' : '0px';
          icon.style.marginRight = i === 1 && iconClasses.length === 3 ? '0px' : '7px';
          el.appendChild(icon);
        }
      });

      el.appendChild(document.createTextNode(arg.event.title));
      return { domNodes: [el] };
    }
  };
  calendarColumns: string[] = [
    'SiteName',
    'SiteCode',
    'DeliveryLocationName',
    'OrderId',
    'OrderSequence',
    'ItemDescription',
    'ItemDescription_C',
    'ItemDescription_S',
    'ItemDescription_CS',
    'ItemSequence',
    'ItemStatus',
    'ItemType',
    'InstanceId',
    'InstanceSequence',
    'InstanceStatus',
    'Insect',
    'ContainerName',
    'SubstrateName',
    'DeliveryDate',
    'Note'
  ];
  itemDescName: string = 'ItemDescription';

  ngOnInit(): void {
    this.appUser = this.appInit.appUser;
    if (this.appUser) {
      this.userSettingsJson =
        !isStringEmpty(this.appUser.userSettingsJson) || isStringEqual(this.appUser.userSettingsJson, '{}')
          ? JSON.parse(this.appUser.userSettingsJson)
          : {};

      this.itemDescName = getItemDescriptionColumn(this.userSettingsJson);
    }

    const request = {
      profile: this.graphService.getMyProfile$(),
      undeliverableDates: this.dictionaryCache.get('UndeliverableDates'),
      undeliverableDays: this.dictionaryCache.get('UndeliverableDays')
    };

    firstValueFrom(forkJoin(request))
      .then((response) => {
        if (response.profile) {
          this.pageVars.profile = response.profile;
          this.pageVars.currentUser = response.profile.mail;
          this.isInitialLoadComplete = true;
        }
        if (response.undeliverableDates.length > 0) {
          this.pageVars.undeliveravbleDates = response.undeliverableDates.map((x: any) => x.text).map((x: string) => this.formatDate(new Date(x)));
          this.pageVars.undeliveravbleDays = [...new Set(response.undeliverableDays.map((x) => x.text))];
        }
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'RequesterCalendar', action: 'InitLoad:Error' });
      });
  }

  loadCalendar(filters: ISearchRequestFilter[]) {
    this.tableFilters = filters;
    this.loadEventsForDateRange(this.greaterthanDate, this.lessThandate);
  }

  loadEventsForDateRange(greaterthanDate: string, lessThandate: string) {
    this.searchRequest.filters = [...this.tableFilters];
    this.searchRequest.sorts = [<ISearchSort>{ name: 'DeliveryDate', direction: 'ASC' }];

    this.searchRequest.filters.push(<ISearchRequestFilter>{
      fieldname: 'DeliveryDate',
      logic: 'and',
      values: [
        <ISearchFilterValue>{ operator: 'GreaterThan', value: greaterthanDate },
        <ISearchFilterValue>{ operator: 'LessThan', value: lessThandate }
      ]
    });

    this.executeSearch();
  }

  calViewChanged(event: any) {
    this.calendar.getApi().changeView(event.value);
  }

  executeSearch() {
    this.searchRequest.columns = [...this.calendarColumns, this.itemDescName];
    firstValueFrom(this.requesterDashboardService.getCalendar(this.searchRequest))
      .then((response) => {
        this.pageVars.orderItemInstances = JSON.parse(response?.results ?? `[]`).map(
          (instance: any) =>
            <any>{
              ...instance,
              allDay: true,
              title: this.instanceTitle(instance),
              date: instance.DeliveryDate,
              id: instance.InstanceId,
              backgroundColor: this.instanceColor(instance),
              borderColor: this.instanceBorderColor(instance),
              extendedProps: {
                icons: this.instanceIcons(instance)
              }
            }
        );

        this.calendarOptions = { ...this.calendarOptions, events: [...this.pageVars.orderItemInstances] };
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'RequesterCalendar', action: 'LoadInstances:Error' });
      });
  }

  private formatDate(dt: Date): string {
    const year = dt.getFullYear().toString();
    const month = (dt.getMonth() + 1).toString().padStart(2, '0');
    const day = dt.getDate().toString().padStart(2, '0');
    return year + '-' + month + '-' + day;
  }

  instanceIcons(instance: any): string[] {
    const icons: string[] = [];
    if (['Draft', 'Submitted'].includes(instance.InstanceStatus)) {
      icons.push('fa-pen-to-square');
    } else if (instance.InstanceStatus === 'Approved') {
      icons.push('fa-circle-check');
    } else if (instance.InstanceStatus === 'Denied') {
      icons.push('fa-circle-xmark');
    } else if (instance.InstanceStatus === 'Cancelled') {
      icons.push('fa-circle-xmark');
    } else if (instance.InstanceStatus === 'Delivered') {
      icons.push('fa-box');
    } else if (instance.InstanceStatus === 'Completed') {
      icons.push('fa-flag-checkered');
    } else if (instance.InstanceStatus === 'Delayed') {
      icons.push('fa-calendar-xmark');
    }

    if (instance.ItemType === 'Insect') {
      icons.push('fa-bug');
    } else if (instance.ItemType === 'Media') {
      icons.push('fa-flask');
    } else if (instance.ItemType === 'Insect + Media') {
      icons.push('fa-bug', 'fa-flask');
    }
    return icons;
  }
  instanceTitle(instance: any): string {
    const title = ['Insect', 'Insect + Media'].includes(instance.ItemType) ? instance.Insect : instance.SubstrateName;
    if (title.length > 20) {
      return title.substring(0, 20) + '...';
    } else {
      return title;
    }
  }
  instanceColor(instance: any): string {
    return instance.ItemType === 'Insect'
      ? '#4271a2ff'
      : instance.ItemType === 'Media'
        ? 'rgba(96, 119, 40, 1)'
        : instance.ItemType === 'Insect + Media'
          ? '#e0a21eff'
          : '';
  }
  instanceBorderColor(instance: any): string {
    return instance.ItemType === 'Insect'
      ? '#4271a2ff'
      : instance.ItemType === 'Media'
        ? 'rgba(96, 119, 40, 1)'
        : instance.ItemType === 'Insect + Media'
          ? '#e0a21eff'
          : '';
  }
}
